import React, { useRef, useState } from "react";
import logo from "../../../icons/latest-logo.png";
import {
  MEAL_SECTIONS,
  normalizeMealPlanIntakeForm,
  emptyMealOption,
} from "./mealPlanIntakeFormData";
import MealPlanOptionEditor from "./MealPlanOptionEditor";
import { displayMealImageSrc } from "./mealPlanImageResolve";

import EditableListEditor from "./EditableListEditor";
import "./MealPlanIntakeForm.css";

const display = (val) =>
  val != null && String(val).trim() !== "" ? String(val) : "\u00a0";

const DISCLAIMER =
  "You are advised to follow all recommendations in this diet plan on your own will. We are not responsible for any specific outcomes.";

function PrintFooter({ page, total }) {
  return (
    <div className="mpi-print-footer">
      <span>VOSTRO WORLD • PERSONALISED DIET PLAN</span>
      <span>
        PAGE {page} OF {total}
      </span>
    </div>
  );
}

function PrintMasthead({ title, subtitle }) {
  return (
    <div className="mpi-mast">
      <img src={logo} alt="Vostro World" className="mpi-logo" crossOrigin="anonymous" />
      <div className="mpi-mast-title">
        <div className="mpi-mast-main">{title}</div>
        {subtitle ? <div className="mpi-mast-sub">{subtitle}</div> : null}
      </div>
    </div>
  );
}

function SectionBar({ children }) {
  return <div className="mpi-bar">{children}</div>;
}

function LineField({ label, value, wide }) {
  return (
    <div className={`mpi-field ${wide ? "mpi-field-wide" : ""}`}>
      <span>{label}</span>
      <strong>{display(value) || "\u00a0"}</strong>
    </div>
  );
}

function optionHasContent(option) {
  return (
    String(option?.text || "").trim() !== "" ||
    !!option?.imageUrl ||
    !!option?.imagePreview
  );
}

function optionsForPrint(section, data) {
  return (data[section.optionsKey] || []).filter(optionHasContent);
}

function sectionHeading(section, data) {
  const label = String(data[section.labelKey] || section.defaultLabel).trim();
  const time = String(data[section.timeKey] || "").trim();
  return time ? `${label} (${time})` : label;
}

function OptionCard({ option, number }) {
  const preview = displayMealImageSrc(option?.imagePreview || option?.imageUrl);
  const text = String(option?.text || "").trim();
  return (
    <div className={`mpi-opt ${preview ? "" : "mpi-opt--text"}`}>
      <div className="mpi-opt-copy">
        <div className="mpi-opt-label">OPTION {number}</div>
        <div className="mpi-opt-text">{text || "\u00a0"}</div>
      </div>
      {preview ? (
        <div className="mpi-opt-photo has-img">
          <img src={preview} alt="" width="90" height="68" />
        </div>
      ) : null}
    </div>
  );
}

function ProfilePage({ data }) {
  const dos = (data.dos_list || []).map((t) => t.trim()).filter(Boolean);
  const donts = (data.donts_list || []).map((t) => t.trim()).filter(Boolean);
  return (
    <>
      <PrintMasthead title="PERSONALISED DIET PLAN" subtitle="HEALTH | FITNESS | WELLNESS" />
      <p className="mpi-disclaimer">{DISCLAIMER}</p>
      <SectionBar>CLIENT PROFILE</SectionBar>
      <div className="mpi-profile-row mpi-profile-3">
        <LineField label="CLIENT NAME:" value={data.name} />
        <LineField label="AGE / GENDER:" value={data.age_gender} />
        <LineField label="DATE:" value={data.assessment_date} />
      </div>
      <div className="mpi-profile-row mpi-profile-4">
        <LineField label="HEIGHT:" value={data.height} />
        <LineField label="WEIGHT:" value={data.weight} />
        <LineField label="BMI:" value={data.bmi} />
        <LineField label="TOTAL BODY FAT %:" value={data.body_fat_pct} />
      </div>
      <div className="mpi-profile-row mpi-profile-2">
        <LineField label="LIFESTYLE ACTIVITY LEVEL:" value={data.lifestyle} />
        <LineField label="FOOD ALLERGIES / INTOLERANCES:" value={data.food_allergy} />
      </div>
      <LineField label="MEDICAL HISTORY / CONDITIONS:" value={data.medical_history} wide />
      <LineField label="PRIMARY FITNESS & HEALTH GOALS:" value={data.goals} wide />

      <SectionBar>DAILY NUTRITIONAL TARGETS</SectionBar>
      <LineField label="GENERAL DIETARY RECOMMENDATIONS:" value={data.recommendations} wide />
      <div className="mpi-macros">
        <div><span>CALORIES REQUIRED</span><strong>{display(data.calories_required) || "\u00a0"}</strong></div>
        <div><span>CARBOHYDRATES (G)</span><strong>{display(data.carbs_grams) || "\u00a0"}</strong></div>
        <div><span>PROTEINS (G)</span><strong>{display(data.proteins_grams) || "\u00a0"}</strong></div>
        <div><span>FATS / LIPIDS (G)</span><strong>{display(data.fats_grams) || "\u00a0"}</strong></div>
      </div>

      <SectionBar>GENERAL DIETARY GUIDELINES</SectionBar>
      <div className="mpi-guidelines">
        <div className="mpi-guide mpi-guide-do">
          <h4>DO&apos;S</h4>
          <ul>
            {dos.map((item, i) => (
              <li key={`do-${i}`}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="mpi-guide mpi-guide-dont">
          <h4>DON&apos;TS</h4>
          <ul>
            {donts.map((item, i) => (
              <li key={`dont-${i}`}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

/** Pack meal options onto as few A4 pages as possible, in the template order. */
function packMealPages(data) {
  const OPTION_H = 22;
  const BAR_H = 8;
  const NOTES_H = 16;
  const USABLE = 252;
  const pages = [];
  let blocks = [];
  let used = 0;

  const flush = () => {
    if (!blocks.length) return;
    pages.push(blocks);
    blocks = [];
    used = 0;
  };

  const need = (height) => {
    if (used > 0 && used + height > USABLE) flush();
  };

  MEAL_SECTIONS.forEach((section) => {
    const options = optionsForPrint(section, data);
    if (!options.length) return;
    need(BAR_H + OPTION_H);
    blocks.push({ type: "bar", title: sectionHeading(section, data) });
    used += BAR_H;
    options.forEach((option, index) => {
      if (index > 0) need(OPTION_H);
      blocks.push({ type: "opt", option, number: index + 1 });
      used += OPTION_H;
    });
  });

  const notes = String(data.closing_notes || "").trim();
  if (notes) {
    need(NOTES_H);
    blocks.push({ type: "notes", text: notes });
    used += NOTES_H;
  }
  flush();
  return pages.length ? pages : [[{ type: "notes", text: "" }]];
}

function MealBlocks({ blocks }) {
  return blocks.map((block, index) => {
    if (block.type === "bar") {
      return <SectionBar key={`bar-${index}`}>{block.title}</SectionBar>;
    }
    if (block.type === "opt") {
      return <OptionCard key={`opt-${index}`} option={block.option} number={block.number} />;
    }
    return (
      <div key={`notes-${index}`} className="mpi-notes">
        <div>SPECIAL INSTRUCTIONS / NUTRITIONIST NOTES</div>
        <p>{block.text || "\u00a0"}</p>
      </div>
    );
  });
}

function buildPrintPages(data) {
  const mealPages = packMealPages(data);
  const total = 1 + mealPages.length;
  const pages = [
    <div key="profile" className="mpi-print-page">
      <div className="mpi-print-body">
        <ProfilePage data={data} />
      </div>
      <PrintFooter page={1} total={total} />
    </div>,
  ];

  mealPages.forEach((blocks, index) => {
    pages.push(
      <div key={`meals-${index}`} className="mpi-print-page">
        <div className="mpi-print-body">
          <PrintMasthead
            title="MEAL SCHEDULE & OPTIONS"
            subtitle="FOLLOW OPTIONS ALTERNATELY"
          />
          <MealBlocks blocks={blocks} />
        </div>
        <PrintFooter page={index + 2} total={total} />
      </div>
    );
  });

  return pages;
}

export function MealPlanIntakeFormPrint({ form }) {
  const data = normalizeMealPlanIntakeForm(form);
  return <div className="mpi-print-root">{buildPrintPages(data)}</div>;
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="form-label mb-1">{label}</label>
      <input
        type={type}
        className="form-control"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, rows = 3 }) {
  return (
    <div className="mpi-screen-section">
      <label className="form-label mb-1">{label}</label>
      <textarea
        className="form-control"
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

const PAGE_TABS = [
  { id: 1, label: "Page 1 — Client profile" },
  { id: 2, label: "Page 2 — Meal schedule" },
];

function MealSectionEditor({ section, data, onChange, branchId }) {
  const { optionsKey, labelKey, timeKey } = section;
  const options = data[optionsKey];

  const setField = (field, value) => onChange({ ...data, [field]: value });

  const setOption = (index, value) => {
    const next = [...options];
    next[index] = value;
    onChange({ ...data, [optionsKey]: next });
  };

  const addOption = () => {
    onChange({ ...data, [optionsKey]: [...options, emptyMealOption()] });
  };

  const removeOption = (index) => {
    if (options.length <= 1) return;
    onChange({ ...data, [optionsKey]: options.filter((_, i) => i !== index) });
  };

  return (
    <div className="mpi-meal-section-editor">
      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <label className="form-label mb-1">Meal title</label>
          <input
            className="form-control"
            value={data[labelKey] || ""}
            onChange={(e) => setField(labelKey, e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label mb-1">Time / schedule (editable)</label>
          <input
            className="form-control"
            placeholder="e.g. 9:00 am"
            value={data[timeKey] || ""}
            onChange={(e) => setField(timeKey, e.target.value)}
          />
        </div>
      </div>

      {options.map((opt, idx) => (
        <MealPlanOptionEditor
          key={`${optionsKey}-${idx}`}
          optionNumber={idx + 1}
          value={opt}
          canRemove={options.length > 1}
          onRemove={() => removeOption(idx)}
          onChange={(val) => setOption(idx, val)}
          branchId={branchId}
        />
      ))}

      <button
        type="button"
        className="btn btn-sm btn-outline-primary mpi-editor-only"
        onClick={addOption}
      >
        <i className="bi bi-plus-lg me-1" />
        Add option
      </button>

      {section.note ? <p className="small text-muted mt-2">{section.note}</p> : null}
    </div>
  );
}

export default function MealPlanIntakeForm({ form, onChange, printRef, branchId }) {
  const data = form?.pre_breakfast_options ? form : normalizeMealPlanIntakeForm(form);
  const localPrintRef = useRef(null);
  const hostRef = printRef || localPrintRef;
  const [activePage, setActivePage] = useState(1);

  const setField = (field, value) => onChange({ ...data, [field]: value });

  return (
    <>
      <div className="mpi-screen-root">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <img src={logo} alt="VOSTRO WORLD" style={{ height: 48 }} />
          <span className="badge bg-danger">Diet plan editor</span>
        </div>

        <div className="mpi-page-tabs">
          {PAGE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activePage === tab.id ? "active" : ""}
              onClick={() => setActivePage(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activePage === 1 ? (
          <>
            <p className="small text-muted">{DISCLAIMER}</p>
            <div className="mpi-screen-grid-2">
              <Field label="Name" value={data.name} onChange={(v) => setField("name", v)} />
              <Field label="Age/Gender" value={data.age_gender} onChange={(v) => setField("age_gender", v)} />
              <Field label="Height" value={data.height} onChange={(v) => setField("height", v)} />
              <Field label="Weight" value={data.weight} onChange={(v) => setField("weight", v)} />
              <Field label="BMI" value={data.bmi} onChange={(v) => setField("bmi", v)} />
              <Field label="Total Body Fat %" value={data.body_fat_pct} onChange={(v) => setField("body_fat_pct", v)} />
              <Field label="Lifestyle" value={data.lifestyle} onChange={(v) => setField("lifestyle", v)} />
              <Field label="Date (first assessment)" type="date" value={data.assessment_date} onChange={(v) => setField("assessment_date", v)} />
            </div>
            <TextAreaField label="Medical History" value={data.medical_history} onChange={(v) => setField("medical_history", v)} />
            <TextAreaField label="Food Allergy" value={data.food_allergy} onChange={(v) => setField("food_allergy", v)} />
            <TextAreaField label="Goals" value={data.goals} onChange={(v) => setField("goals", v)} />
            <TextAreaField label="Recommendations" value={data.recommendations} onChange={(v) => setField("recommendations", v)} />
            <div className="mpi-screen-grid-2">
              <Field label="Carbohydrates (grams)" value={data.carbs_grams} onChange={(v) => setField("carbs_grams", v)} />
              <Field label="Proteins (grams)" value={data.proteins_grams} onChange={(v) => setField("proteins_grams", v)} />
              <Field label="Lipids / Fats (grams)" value={data.fats_grams} onChange={(v) => setField("fats_grams", v)} />
              <Field label="Calories required" value={data.calories_required} onChange={(v) => setField("calories_required", v)} />
            </div>
            <div className="row mt-3">
              <div className="col-md-6">
                <EditableListEditor
                  label="Do's"
                  items={data.dos_list}
                  onChange={(list) => setField("dos_list", list)}
                  placeholder="Add a do..."
                  addLabel="Add do"
                />
              </div>
              <div className="col-md-6">
                <EditableListEditor
                  label="Don'ts"
                  items={data.donts_list}
                  onChange={(list) => setField("donts_list", list)}
                  placeholder="Add a don't..."
                  addLabel="Add don't"
                />
              </div>
            </div>
            <p className="small text-muted mt-2 mpi-editor-only">
              Default do&apos;s and don&apos;ts are pre-filled. Remove any line or add custom items with the + button.
            </p>
          </>
        ) : null}

        {activePage === 2 ? (
          <>
            {MEAL_SECTIONS.map((section) => (
              <div key={section.key} className="mpi-screen-section">
                <h6 className="mpi-screen-meal-title">{section.defaultLabel}</h6>
                <MealSectionEditor
                  section={section}
                  data={data}
                  onChange={onChange}
                  branchId={branchId}
                />
              </div>
            ))}
            <TextAreaField
              label="Special instructions / nutritionist notes"
              value={data.closing_notes}
              onChange={(v) => setField("closing_notes", v)}
              rows={3}
            />
          </>
        ) : null}
      </div>

      <div className="mpi-print-host" ref={hostRef}>
        <MealPlanIntakeFormPrint form={data} />
      </div>
    </>
  );
}
